


















































































































































import { computed, defineComponent, onBeforeUnmount, PropType, ref, toRef, watch } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Call, CallStatus } from "@scrile/api-provider/dist/api/CallsProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import AudioPlayer from "@/lib/audioPlayer";
import { $t } from "@/lib/i18nHelpers";
import { connectionStateEnum } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import BaseIcon from "@/components/BaseIcon";
import MobileSwitchCam from "@/components/MobileSwitchCam";
import ModalView from "@/components/ModalView";
import CallStateView from "./CallStateView.vue";
import CallStatusView from "./CallStatusView";
import SmallCallModalView from "./SmallCallModalView";

const CLICK_DEBOUNCE_DELAY = 300;

export default defineComponent({
  name: "CallModalView",
  components: {
    SmallCallModalView,
    CallStatusView,
    CallStateView,
    ModalView,
    BaseIcon,
    BaseAvatar,
    BaseButton,
    BaseConfirm,
    MobileSwitchCam,
  },
  props: {
    showConnectionError: { type: Boolean, default: false },
    connectionErrorMessage: { type: String, default: "" },
    opponentUser: { type: Object as PropType<UserPublicData>, required: true },
    call: { type: Object as PropType<Call | null>, default: null },
    opponentStream: { type: MediaStream, required: true },
    localStream: { type: MediaStream, required: true },
    localReadyVideoState: { type: Boolean, required: true },
    opponentReadyVideoState: { type: Boolean, required: true },
    localVideoState: { type: Boolean, required: true },
    opponentVideoState: { type: Boolean, required: true },
    loading: { type: Boolean, default: false },
    connectionState: { type: String as PropType<connectionStateEnum>, default: connectionStateEnum.EMPTY },
    forceSmallView: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const player = ref<HTMLVideoElement>();
    const playerSmall = ref<HTMLVideoElement>();
    const localPlayer = ref<HTMLVideoElement>();
    const isSmallView = ref(props.forceSmallView);
    const isVideoStream = computed(() => props.opponentVideoState && props.localVideoState);

    const processingVideoRequest = computed(() => props.localReadyVideoState && !props.opponentReadyVideoState);

    const showConfirmVideoRequest = computed(() => !props.localReadyVideoState && props.opponentReadyVideoState);

    const processingCall = computed(
      () => props.call?.status === CallStatus.NEW || props.connectionState === connectionStateEnum.EMPTY
    );

    const titleVideoRequest = computed(() => $t("{0} asks for video chat", [props.opponentUser.screenName]));

    const iconVideo = computed(() => (isVideoStream.value ? "sci-video-off" : "sci-video"));

    const showAvatar = computed(() => {
      return (
        !isVideoStream.value &&
        !props.showConnectionError &&
        !processingVideoRequest.value &&
        !processingCall.value &&
        ![connectionStateEnum.CONNECTING, connectionStateEnum.DISCONNECTED].includes(props.connectionState)
      );
    });
    const callEnded = computed(
      () =>
        (props.call && [CallStatus.CANCELLED, CallStatus.DECLINED, CallStatus.ENDED].includes(props.call.status)) ||
        !props.call
    );

    const showVideoButton = computed(
      () => !processingVideoRequest.value && !processingCall.value && !callEnded.value && !props.showConnectionError
    );

    const showHangupButton = computed(() => !callEnded.value || props.showConnectionError);

    const localVideoTracks = computed<MediaStreamTrack[]>(() => props.localStream.getVideoTracks());

    const mirrorVideo = computed(() => {
      const settings = localVideoTracks.value.length > 0 && localVideoTracks.value[0].getSettings();
      return !settings || !settings.facingMode || settings.facingMode === "user";
    });

    const showSwitchCam = computed(
      () => localVideoTracks.value.length > 0 && !processingVideoRequest.value && isVideoStream.value
    );

    const disabledVideoButton = computed(
      () => props.loading || props.opponentReadyVideoState !== props.localReadyVideoState
    );

    const onVideoToggle = debounce(() => {
      emit("toggleVideo");
    }, CLICK_DEBOUNCE_DELAY);

    const onAgreeClick = debounce(() => {
      emit("videoRequestAccept");
    }, CLICK_DEBOUNCE_DELAY);

    const onChangeDeviceClick = debounce((constraints: MediaStreamConstraints) => {
      if (!isVideoStream.value) {
        return;
      }
      emit("changeDevice", constraints.video);
    }, CLICK_DEBOUNCE_DELAY);

    const onCallStateViewClick = debounce(() => {
      props.showConnectionError ? emit("callBack") : emit("cancelVideoRequest");
    }, CLICK_DEBOUNCE_DELAY);

    const onVideoRequestDeclineClick = debounce(() => {
      emit("videoRequestDecline");
    }, CLICK_DEBOUNCE_DELAY);

    const onOpponentStreamChange = async (opponentStream: MediaStream) => {
      if (!player.value || !playerSmall.value) return;
      player.value.pause();
      playerSmall.value.pause();
      const hasMedia = opponentStream.getTracks().length > 0;
      if (hasMedia) {
        player.value.srcObject = opponentStream;
        playerSmall.value.srcObject = opponentStream;
        await player.value.play();
        await playerSmall.value.play();
      }
    };

    const onLocalStreamChange = async (localStream: MediaStream) => {
      if (!localPlayer.value) return;
      localPlayer.value.pause();
      const hasMedia = localStream.getVideoTracks().length > 0;
      if (hasMedia) {
        localPlayer.value.srcObject = localStream;
        await localPlayer.value.play();
      }
    };

    watch(toRef(props, "opponentStream"), onOpponentStreamChange);
    watch(toRef(props, "localStream"), onLocalStreamChange);

    onBeforeUnmount(() => {
      AudioPlayer.stop("beep");
    });

    return {
      player,
      playerSmall,
      isSmallView,
      localPlayer,
      isVideoStream,
      processingVideoRequest,
      showConfirmVideoRequest,
      processingCall,
      titleVideoRequest,
      iconVideo,
      showAvatar,
      showVideoButton,
      showHangupButton,
      localVideoTracks,
      mirrorVideo,
      showSwitchCam,
      disabledVideoButton,
      onVideoToggle,
      onAgreeClick,
      onChangeDeviceClick,
      onCallStateViewClick,
      onVideoRequestDeclineClick,
    };
  },
});
